import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { IApiError } from '@core/interfaces/api-error.interface';
import { ErrorFormatterService } from '@core/services/error-formatter.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { IAnalyticsNode, ICampaignAnalytics, TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TObservationStatus } from '@pages/campaigns/campaign-details/campaign-details-observations/campaign-details-observations.interface';
import { EKpiGoalsStatusBadge } from '@pages/campaigns/campaign-details/cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details-kpi-goals-table/cta-analytics-details-kpi-goals-table.interface';
import { CampaignsService } from '@pages/campaigns/campaigns.service';
import { ICreator } from '@pages/creators/creators.interface';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EditableTitleComponent } from '@shared/components/editable-title/editable-title.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { PillCheckboxComponent } from '@shared/components/pill-checkbox/pill-checkbox.component';
import { ProfileDateFilterComponent } from '@shared/components/profile-date-filter/profile-date-filter.component';
import {
  EDateRangeType,
  IDateRangeBounds,
  IProfileDateFilterState,
} from '@shared/components/profile-date-filter/profile-date-filter.interface';
import { CampaignDetailsPlatformFeaturesService } from '@shared/components/profile-platform-feature-filter/profile-platform-feature-filter.service';
import { SelectableStatusBadgeComponent } from '@shared/components/selectable-status-badge/selectable-status-badge.component';
import { TTileContentStatus } from '@shared/components/social-content-tile/social-content-tile.interface';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { SignalSetUtilitiesService } from '@utilities/signal-set-utilities.service';
import { camelCase } from 'lodash';
import { filter, map, startWith } from 'rxjs';
import {
  CAMPAIGN_DETAILS_ALL_TABS,
  ECampaignDetailsTab,
  ICampaignAnalyticsFilter,
} from '../../../campaign-details.interface';
import {
  EDetailLevel,
  ICampaignExportApi,
  ICampaignExportContentMatchingTypeApi,
  ICampaignExportContentStatusApi,
  ICampaignExportDeliverableStatusApi,
  ICampaignExportKpiStatusApi,
  ICampaignExportObservationStatusApi,
} from '../campaign-export-dialog.interface';
import { CampaignExportDialogService } from '../campaign-export-dialog.service';
import { CampaignExportDialogStore } from '../campaign-export-dialog.store';
import { CampaignExportService } from '../campaign-export.service';

@Component({
  imports: [
    TranslateModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatExpansionModule,
    MatTooltipModule,
    PillCheckboxComponent,
    IconBadgeComponent,
    IconComponent,
    NgClass,
    SelectableStatusBadgeComponent,
    ProfileDateFilterComponent,
    EditableTitleComponent,
    DotsSpinnerComponent,
    CdkDragHandle,
  ],
  selector: 'app-campaign-export-panel',
  templateUrl: './campaign-export-panel.component.html',
})
export class CampaignExportPanelComponent {
  public readonly campaignExport = input.required<ICampaignExportApi>();
  public readonly campaignAnalytics = input.required<ICampaignAnalytics | null>();
  public readonly analyticsNodes = input.required<IAnalyticsNode[] | null>();
  public readonly platformFeaturesKpis = input.required<TExpectedKpi[] | null>();
  public readonly expanded = input.required<boolean>();
  public readonly dragDisabled = input.required<boolean>();

  public readonly openAttempt = output<void>();
  public readonly closeAttempt = output<void>();
  public readonly saved = output<ICampaignExportApi>();
  public readonly deleteRequested = output<void>();
  public readonly dirtyChange = output<boolean>();
  public readonly nameEditRequested = output<void>();

  public readonly EXPORT_TABS: ECampaignDetailsTab[] = CAMPAIGN_DETAILS_ALL_TABS;

  public readonly DELIVERABLE_STATUSES: EAnalyticsStatus[] = [
    EAnalyticsStatus.failed,
    EAnalyticsStatus.problem,
    EAnalyticsStatus.processing,
    EAnalyticsStatus.ok,
  ];

  public readonly OBSERVATION_STATUSES: TObservationStatus[] = ['noFindings', 'findings', 'processing'];

  public readonly CONTENT_STATUSES: TTileContentStatus[] = ['notFulfilled', 'partiallyFulfilled', 'fulfilled'];

  public readonly KPI_STATUSES: EKpiGoalsStatusBadge[] = [
    EKpiGoalsStatusBadge.kpiGoalsUnavailable,
    EKpiGoalsStatusBadge.kpiGoalsBelowTarget,
    EKpiGoalsStatusBadge.kpiGoalsReached,
  ];

  public readonly CONTENT_MATCHING_TYPES: EMatchingType[] = [
    EMatchingType.missing,
    EMatchingType.bonus,
    EMatchingType.planned,
    EMatchingType.matched,
    EMatchingType.unrelated,
  ];

  /** Maps observation display status → API EAnalyticsStatus */
  private readonly OBSERVATION_STATUS_TO_API: Record<TObservationStatus, EAnalyticsStatus> = {
    findings: EAnalyticsStatus.ok,
    noFindings: EAnalyticsStatus.failed,
    processing: EAnalyticsStatus.processing,
  };

  /** Maps API EAnalyticsStatus → observation display status */
  private readonly API_TO_OBSERVATION_STATUS: Partial<Record<EAnalyticsStatus, TObservationStatus>> = {
    [EAnalyticsStatus.failed]: 'noFindings',
    [EAnalyticsStatus.ok]: 'findings',
    [EAnalyticsStatus.processing]: 'processing',
  };

  /** Maps content display status → API EAnalyticsStatus */
  private readonly CONTENT_STATUS_TO_API: Record<TTileContentStatus, EAnalyticsStatus> = {
    fulfilled: EAnalyticsStatus.ok,
    notFulfilled: EAnalyticsStatus.failed,
    partiallyFulfilled: EAnalyticsStatus.problem,
  };

  /** Maps API EAnalyticsStatus → content display status */
  private readonly API_TO_CONTENT_STATUS: Partial<Record<EAnalyticsStatus, TTileContentStatus>> = {
    [EAnalyticsStatus.failed]: 'notFulfilled',
    [EAnalyticsStatus.problem]: 'partiallyFulfilled',
    [EAnalyticsStatus.ok]: 'fulfilled',
  };

  /** Maps KPI goal display status → API EAnalyticsStatus */
  private readonly KPI_STATUS_TO_API: Record<EKpiGoalsStatusBadge, EAnalyticsStatus | null> = {
    [EKpiGoalsStatusBadge.kpiGoalsUnavailable]: EAnalyticsStatus.failed,
    [EKpiGoalsStatusBadge.kpiGoalsBelowTarget]: EAnalyticsStatus.problem,
    [EKpiGoalsStatusBadge.kpiGoalsReached]: EAnalyticsStatus.ok,
    [EKpiGoalsStatusBadge.kpiGoalsFailed]: EAnalyticsStatus.failed,
    [EKpiGoalsStatusBadge.kpiGoalsPlanned]: null,
    [EKpiGoalsStatusBadge.kpiGoalsProcessing]: EAnalyticsStatus.processing,
    [EKpiGoalsStatusBadge.kpiGoalsNotStarted]: null,
    [EKpiGoalsStatusBadge.kpiGoalsNotSet]: null,
    [EKpiGoalsStatusBadge.kpiGoalsHealthy]: EAnalyticsStatus.ok,
  };

  /** Maps API EAnalyticsStatus → KPI goal display status */
  private readonly API_TO_KPI_STATUS: Partial<Record<EAnalyticsStatus, EKpiGoalsStatusBadge>> = {
    [EAnalyticsStatus.failed]: EKpiGoalsStatusBadge.kpiGoalsUnavailable,
    [EAnalyticsStatus.problem]: EKpiGoalsStatusBadge.kpiGoalsBelowTarget,
    [EAnalyticsStatus.ok]: EKpiGoalsStatusBadge.kpiGoalsReached,
  };

  public readonly TAB_TRANSLATION_KEYS: Record<ECampaignDetailsTab, string> = {
    [ECampaignDetailsTab.overview]: 'pages.campaignDetails.tabs.overview',
    [ECampaignDetailsTab.performance]: 'pages.campaignDetails.tabs.kpis',
    [ECampaignDetailsTab.kpiGoals]: 'pages.campaignDetails.tabs.kpiGoals',
    [ECampaignDetailsTab.deliverables]: 'pages.campaignDetails.tabs.deliverables',
    [ECampaignDetailsTab.observations]: 'pages.campaignDetails.tabs.observations',
    [ECampaignDetailsTab.contents]: 'pages.campaignDetails.tabs.contents',
    [ECampaignDetailsTab.creators]: 'pages.campaignDetails.tabs.creators',
    [ECampaignDetailsTab.creatorOverview]: 'pages.campaignDetails.tabs.overview',
    [ECampaignDetailsTab.creatorTimeline]: 'pages.campaignDetails.tabs.timeline',
  };

  public readonly allKpisSelected = signal<boolean>(false);
  public readonly allCreatorsSelected = signal<boolean>(false);
  public readonly allPlatformFeaturesSelected = signal<boolean>(false);
  public readonly allTabsSelected = signal<boolean>(false);
  public readonly expandedPanels = signal<Set<string>>(new Set<string>());

  public readonly exportSelectedDateRange = signal<EDateRangeType>(EDateRangeType.allTime);
  public readonly exportStartDate = signal<Date | null>(null);
  public readonly exportEndDate = signal<Date | null>(null);

  public readonly exportDateFilterInitialState = computed<IProfileDateFilterState | null>(() => {
    if (this.exportSelectedDateRange() === EDateRangeType.custom && this.exportStartDate() && this.exportEndDate()) {
      return {
        endDate: this.exportEndDate()!,
        selectedRange: EDateRangeType.custom,
        startDate: this.exportStartDate()!,
      };
    }

    return null;
  });

  public readonly exportDateRangeBounds = computed<IDateRangeBounds | null>(() => {
    const campaignAnalytics = this.campaignAnalytics();

    if (!campaignAnalytics) {
      return null;
    }

    return { end: campaignAnalytics.end as Date, start: campaignAnalytics.start as Date };
  });

  public readonly linkCopiedTimeout = signal<boolean>(false);
  public readonly formChanged = signal<boolean>(false);
  public readonly saving = signal<boolean>(false);
  /** Bumped on every selection change so computed signals depending on plain Set mutations re-evaluate. */
  private readonly changeTick = signal<number>(0);

  /** Snapshot of the name/budget/detailLevel values as of the last clean state (initial load, post-save,
   *  or discard) — compared against live form values so dirtiness is independent of `form.dirty`, which
   *  is only reliably set by DOM-driven control interaction and not by programmatic `setValue()` calls. */
  private readonly baselineName = signal<string>('');
  private readonly baselineBudget = signal<boolean>(false);
  private readonly baselineDetailLevel = signal<EDetailLevel>(EDetailLevel.generalInfo);

  public readonly nameDirty = computed(() => (this.generalFormValue()?.name || '') !== this.baselineName());
  public readonly budgetDirty = computed(() => !!this.generalFormValue()?.budget !== this.baselineBudget());
  public readonly detailLevelDirty = computed(
    () => (this.generalFormValue()?.detailLevel || EDetailLevel.generalInfo) !== this.baselineDetailLevel(),
  );

  public readonly isValueDirty = computed(() => {
    this.changeTick();

    return this.formChanged() || this.nameDirty() || this.budgetDirty() || this.detailLevelDirty();
  });

  /** True while the report name is being edited. Merely opening the editor isn't itself a change
   *  worth guarding against losing — `isValueDirty()` already catches an actual edit, since typing
   *  updates `nameControl`'s live value in real time. This is only used to silently close an
   *  untouched, still-open editor where nothing would actually be lost (see `onCancelClick`) and
   *  to restore edit mode if a close gets reversed (see `onPanelClosed`/`reaffirmExpanded`). */
  public readonly nameEditModeActive = signal<boolean>(false);

  public readonly expansionSettled = signal<boolean>(false);

  public readonly store = inject(CampaignExportDialogStore);

  /** True while THIS report is saving, or ANY report in the dialog has an add/delete/save/unpublish request in flight. */
  public readonly controlsDisabled = computed(() => this.saving() || this.store.busy$());
  public readonly publishClicked = signal<boolean>(false);
  public readonly unpublishClicked = signal<boolean>(false);
  public readonly saveClicked = signal<boolean>(false);

  public readonly allVisibleDataExpanded = computed(() => this.expandedPanels().size === this.PANEL_IDS.length);

  public readonly platformFeatures = computed<EPlatformFeature[]>(() => {
    const campaignAnalytics = this.campaignAnalytics();

    if (!campaignAnalytics) {
      return [];
    }

    return Array.from(this.campaignDetailsPlatformFeaturesService.getCampaignPlatformFeatures(campaignAnalytics));
  });

  public readonly kpis = computed(() => this.platformFeaturesKpis() ?? []);

  public readonly creators = computed(() => {
    const campaignAnalytics = this.campaignAnalytics();

    if (!campaignAnalytics) {
      return [];
    }

    return this.campaignExportDialogService.getCreators(campaignAnalytics);
  });

  public readonly form = new FormGroup({
    budget: new FormControl<boolean | null>(null),
    detailLevel: new FormControl<EDetailLevel | null>(null),
    link: new FormControl<string | null>(null),
    name: new FormControl<string | null>(null),
  });
  public readonly kpiSearchControl = new FormControl<string | null>(null);
  public readonly creatorSearchControl = new FormControl<string | null>(null);
  public readonly EDetailLevel = EDetailLevel;

  public readonly kpiQuery = toSignal(
    this.kpiSearchControl.valueChanges.pipe(
      startWith(''),
      map((v) => (v ?? '').trim().toLowerCase()),
    ),
    { initialValue: '' },
  );

  public readonly creatorQuery = toSignal(
    this.creatorSearchControl.valueChanges.pipe(
      startWith(''),
      map((v) => (v ?? '').trim().toLowerCase()),
    ),
    { initialValue: '' },
  );

  public readonly filteredKpis = computed<TExpectedKpi[]>(() => {
    const query = this.kpiQuery();
    const list = this.kpis();

    if (!query) {
      return list as TExpectedKpi[];
    }

    return list.filter((kpi) => kpi.toLowerCase().includes(query)) as TExpectedKpi[];
  });

  public readonly filteredCreators = computed<ICreator[]>(() => {
    const query = this.creatorQuery();
    const list = this.creators();

    if (!query) {
      return list as ICreator[];
    }

    return list.filter((creator) => (creator?.name || '').toLowerCase().includes(query)) as ICreator[];
  });

  public readonly generalFormValue = toSignal(this.form.valueChanges.pipe(startWith(this.form.value)));
  public readonly selectedGeneralCount = computed(() => {
    const v = this.generalFormValue();

    return [v?.budget].filter(Boolean).length;
  });
  public readonly TOTAL_GENERAL_TOGGLES = 1 as const;

  public selectedKpis = new Set<TExpectedKpi>();
  public selectedCreatorIds = new Set<string>();
  public selectedPlatformFeatures = new Set<EPlatformFeature>();
  public selectedTabs = new Set<ECampaignDetailsTab>();
  public selectedDeliverableStatuses = new Set<EAnalyticsStatus>();
  public selectedObservationStatuses = new Set<TObservationStatus>();
  public selectedContentStatuses = new Set<TTileContentStatus>();
  public selectedKpiStatuses = new Set<EKpiGoalsStatusBadge>();
  public selectedContentMatchingTypes = new Set<EMatchingType>();

  public readonly EAnalyticsStatus = EAnalyticsStatus;
  public readonly EMatchingType = EMatchingType;
  public readonly EKpiGoalsStatusBadge = EKpiGoalsStatusBadge;

  public readonly nameControl = this.form.controls.name;

  public readonly timeRangeBadge = computed<string>(() => {
    this.formChanged();

    if (this.exportSelectedDateRange() === EDateRangeType.custom && this.exportStartDate() && this.exportEndDate()) {
      const start = this.dateUtilitiesService.formatDate(this.exportStartDate(), 'd MMM yyyy');
      const end = this.dateUtilitiesService.formatDate(this.exportEndDate(), 'd MMM yyyy');

      return `${start} - ${end}`;
    }

    return this.translate.instant('pages.campaignDetails.export.fullCampaignRange');
  });

  public readonly insightLevelBadge = computed<string>(() => {
    // Read via the reactive `generalFormValue` signal (not `form.get(...).value` directly) so
    // this computed actually has a tracked dependency and recomputes when the value changes.
    const detailLevel = this.generalFormValue()?.detailLevel ?? EDetailLevel.generalInfo;
    const translationKeys: Record<EDetailLevel, string> = {
      [EDetailLevel.generalInfo]: 'pages.campaignDetails.export.overviewOnly',
      [EDetailLevel.generalAnalytics]: 'pages.campaignDetails.export.analyticsSummary',
      [EDetailLevel.detailedAnalytics]: 'pages.campaignDetails.export.fullAnalytics',
    };

    return this.translate.instant(translationKeys[detailLevel]);
  });

  public readonly insightLevelIcon = computed<string>(() => {
    const detailLevel = this.generalFormValue()?.detailLevel ?? EDetailLevel.generalInfo;
    const icons: Record<EDetailLevel, string> = {
      [EDetailLevel.generalInfo]: '/icons/magnify.svg',
      [EDetailLevel.generalAnalytics]: '/icons/analytics.svg',
      [EDetailLevel.detailedAnalytics]: '/icons/infinity.svg',
    };

    return icons[detailLevel];
  });

  public readonly customConfigsCount = computed<number>(() => {
    this.changeTick();

    const totalStatusCount =
      this.selectedDeliverableStatuses.size +
      this.selectedObservationStatuses.size +
      this.selectedContentStatuses.size +
      this.selectedKpiStatuses.size;
    const allStatusCount =
      this.DELIVERABLE_STATUSES.length +
      this.OBSERVATION_STATUSES.length +
      this.CONTENT_STATUSES.length +
      this.KPI_STATUSES.length;

    const customFlags = [
      this.selectedTabs.size !== this.EXPORT_TABS.length,
      // Budget's default is off, not "all toggles on" like every other general item here, so it
      // can't share the `selectedGeneralCount() !== TOTAL_GENERAL_TOGGLES` pattern — that would
      // flag every fresh, untouched report as "customized" simply for having budget off.
      this.selectedContentMatchingTypes.size !== this.CONTENT_MATCHING_TYPES.length,
      totalStatusCount !== allStatusCount,
      this.selectedPlatformFeatures.size !== this.platformFeatures().length,
      this.selectedKpis.size !== this.kpis().length,
      this.selectedCreatorIds.size !== this.creators().length,
    ];

    return customFlags.filter(Boolean).length;
  });

  public readonly customConfigsBadge = computed<string>(() => {
    const count = this.customConfigsCount();

    if (count === 0) {
      return this.translate.instant('pages.campaignDetails.export.noCustomConfigs');
    }

    return this.translate.instant('pages.campaignDetails.export.customConfigs', { count });
  });

  private readonly campaignExportService = inject(CampaignExportService);
  private readonly campaignExportDialogService = inject(CampaignExportDialogService);
  private readonly campaignsService = inject(CampaignsService);
  private readonly campaignDetailsPlatformFeaturesService = inject(CampaignDetailsPlatformFeaturesService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly dateUtilitiesService = inject(DateUtilitiesService);
  private readonly errorFormatterService = inject(ErrorFormatterService);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly router = inject(Router);

  public readonly nameNotUnique = computed(() => {
    const name = (this.generalFormValue()?.name || '').trim();

    if (!name) {
      return false;
    }

    const currentId = this.campaignExport().id;
    const lowerName = name.toLowerCase();

    return this.store
      .campaignExports$()
      .some((campaignExport) => campaignExport.id !== currentId && campaignExport.name?.toLowerCase() === lowerName);
  });

  public readonly nameErrorMessage = computed<string | null>(() =>
    this.nameNotUnique() ? this.translate.instant('pages.campaignDetails.export.nameNotUniqueError') : null,
  );

  private readonly PANEL_IDS = ['tabs', 'general', 'platforms', 'kpis', 'creators', 'contentTypes', 'status'] as const;

  private readonly matExpansionPanel = viewChild.required(MatExpansionPanel);
  private readonly editableTitle = viewChild.required(EditableTitleComponent);

  private initialized = false;
  private hasCheckedInitialExpansion = false;
  private wasEditingNameBeforeClose = false;

  constructor() {
    effect(() => {
      const campaignExport = this.campaignExport();

      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.applyCampaignExport(campaignExport);
    });

    effect(() => {
      const expanded = this.expanded();

      if (this.hasCheckedInitialExpansion) {
        return;
      }

      this.hasCheckedInitialExpansion = true;

      if (expanded) {
        this.expansionSettled.set(true);
      }
    });

    effect(() => {
      this.dirtyChange.emit(this.isValueDirty());
    });

    effect(() => {
      this.nameControl.setErrors(this.nameNotUnique() ? { nameNotUnique: true } : null);
    });

    effect(() => {
      const kpis = this.kpis();

      if (!this.allKpisSelected() || this.selectedKpis.size === kpis.length) {
        return;
      }

      this.selectedKpis = new Set(kpis);
      this.changeTick.update((tick) => tick + 1);
    });

    this.form.get('link')?.disable();
    this.nameSubscription();
  }

  public onNameEditIconClick(): void {
    if (this.expanded()) {
      this.enterNameEditMode();

      return;
    }

    this.nameEditRequested.emit();
  }

  public enterNameEditMode(): void {
    this.editableTitle().enterEditMode();
  }

  public onNameEditModeChanged(active: boolean): void {
    this.nameEditModeActive.set(active);
  }

  public forceCollapse(): void {
    this.matExpansionPanel().close();
  }

  public forceExpand(): void {
    this.matExpansionPanel().open();
  }

  public reaffirmExpanded(): void {
    this.matExpansionPanel().open();
    this.expansionSettled.set(true);

    if (this.wasEditingNameBeforeClose) {
      this.wasEditingNameBeforeClose = false;
      this.editableTitle().enterEditMode();
    }
  }

  public onPanelOpened(): void {
    this.openAttempt.emit();
  }

  public onPanelClosed(): void {
    this.expansionSettled.set(false);
    // The name editor must never remain open in a collapsed panel's header. Remember whether it
    // was active so `reaffirmExpanded()` can restore it if this close turns out to be reversed.
    this.wasEditingNameBeforeClose = this.nameEditModeActive();
    this.editableTitle().exitEditMode();
    this.closeAttempt.emit();
  }

  public onAfterExpand(): void {
    this.expansionSettled.set(true);
  }

  public onSaveClick(): void {
    if (this.form.invalid || this.controlsDisabled()) {
      return;
    }

    this.publishClicked.set(false);
    this.unpublishClicked.set(false);
    this.saveClicked.set(true);
    this.save(this.campaignExport().isActive);
  }

  public onPublish(): void {
    if (this.form.invalid || this.controlsDisabled()) {
      return;
    }

    this.unpublishClicked.set(false);
    this.saveClicked.set(false);
    this.publishClicked.set(true);
    this.save(true);
  }

  public onUnpublish(): void {
    if (this.controlsDisabled()) {
      return;
    }

    this.publishClicked.set(false);
    this.saveClicked.set(false);
    this.unpublishClicked.set(true);
    this.save(false);
  }

  public onCancelClick(): void {
    if (!this.isValueDirty()) {
      if (this.nameEditModeActive()) {
        this.discardChanges();
      }

      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.campaignDetails.export.confirmDialog.goBack'),
        confirmText: this.translate.instant('pages.campaignDetails.export.confirmDialog.discardChanges'),
        message: this.translate.instant('pages.campaignDetails.export.confirmDialog.message'),
        swapButtons: true,
        title: this.translate.instant('pages.campaignDetails.export.confirmDialog.title'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.discardChanges();
      });
  }

  public onDeleteClick(): void {
    if (this.controlsDisabled()) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.delete'),
        message: this.translate.instant('pages.campaignDetails.export.deleteDialog.message', {
          name: this.campaignExport().name,
        }),
        title: this.translate.instant('pages.campaignDetails.export.deleteDialog.title'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.deleteRequested.emit();
      });
  }

  public onCloneClick(): void {
    if (this.controlsDisabled()) {
      return;
    }

    const campaignExport = this.campaignExport();
    const campaignId = this.campaignAnalytics()?.id;

    if (!campaignId) {
      return;
    }

    // Mirrors "Add Report"'s flow: re-fetch the campaign's current exports right before
    // generating the name so the uniqueness check is based on fresh data, not this dialog's
    // possibly-stale local snapshot (e.g. another session created/renamed a report meanwhile).
    this.store.setCreatingExport(true);
    const params = new HttpParams().set('joins', 'campaign_exports');

    this.campaignsService.getCampaign(campaignId, params).subscribe({
      error: () => this.store.setCreatingExport(false),
      next: (campaign) => {
        const existingNames = (campaign?.campaignExports ?? [])
          .map((existingExport) => existingExport.name)
          .filter((name): name is string => !!name);

        this.store.pendingReportCreationSource = 'clone';
        this.store.createCampaignExport({
          ...campaignExport,
          campaignId,
          contentMatchingTypes: this.cloneChildren(campaignExport.contentMatchingTypes),
          contentStatuses: this.cloneChildren(campaignExport.contentStatuses),
          creators: this.cloneChildren(campaignExport.creators),
          deliverableStatuses: this.cloneChildren(campaignExport.deliverableStatuses),
          id: null,
          isActive: false,
          kpis: this.cloneChildren(campaignExport.kpis),
          kpiStatuses: this.cloneChildren(campaignExport.kpiStatuses),
          name: this.generateCloneName(campaignExport.name, existingNames),
          observationStatuses: this.cloneChildren(campaignExport.observationStatuses),
          order: null,
          platformFeatures: this.cloneChildren(campaignExport.platformFeatures),
          tabs: this.cloneChildren(campaignExport.tabs),
        });
      },
    });
  }

  public onCopyLinkClick(displaySnackbar = true): void {
    const link = this.generateLink() || this.form.get('link')?.value;

    if (!link || (!this.campaignExport().isActive && displaySnackbar)) {
      return;
    }

    if (displaySnackbar) {
      this.linkCopiedTimeout.set(true);

      setTimeout(() => {
        this.linkCopiedTimeout.set(false);
      }, 3000);
    }

    navigator.clipboard.writeText(link);

    if (displaySnackbar) {
      this.snackbarService.openSuccess(this.translate.instant('pages.campaignDetails.export.linkCopied'));
      this.eventTrackingService.track(EEventType.campaignReportUrlCopied, {
        campaignId: this.campaignAnalytics()?.id,
        campaignReportId: this.campaignExport().id ?? undefined,
      });
    }
  }

  public discardChanges(): void {
    // Revert any in-progress, unconfirmed name edit first — applyCampaignExport() below then
    // re-patches the form from the authoritative server data, which always wins.
    this.editableTitle().discardEditMode();
    this.applyCampaignExport(this.campaignExport());
    this.formChanged.set(false);
  }

  public getPublishStatusTooltip(): string {
    const campaignExport = this.campaignExport();

    if (!campaignExport.isActive) {
      return this.translate.instant('pages.campaignDetails.export.campaignNotPublished');
    }

    if (campaignExport.isActive && this.isValueDirty()) {
      return this.translate.instant('pages.campaignDetails.export.campaignNeedsRepublishing');
    }

    return this.translate.instant('pages.campaignDetails.export.campaignPublished');
  }

  public onPlatformFeatureChange(checked: boolean, platformFeature: EPlatformFeature): void {
    this.markDirty();

    if (checked) {
      this.selectedPlatformFeatures.add(platformFeature);
    } else {
      this.selectedPlatformFeatures.delete(platformFeature);
    }

    this.allPlatformFeaturesSelected.set(this.selectedPlatformFeatures.size === this.platformFeatures().length);
  }

  public onKpiChange(checked: boolean, kpi: string): void {
    this.markDirty();

    if (checked) {
      this.selectedKpis.add(kpi as TExpectedKpi);
    } else {
      this.selectedKpis.delete(kpi as TExpectedKpi);
    }

    this.allKpisSelected.set(this.selectedKpis.size === this.kpis().length);
  }

  public onCreatorChange(checked: boolean, creatorId: string): void {
    this.markDirty();

    if (checked) {
      this.selectedCreatorIds.add(creatorId);
    } else {
      this.selectedCreatorIds.delete(creatorId);
    }

    this.allCreatorsSelected.set(this.selectedCreatorIds.size === this.creators().length);
  }

  public onTabChange(checked: boolean, tab: ECampaignDetailsTab): void {
    this.markDirty();

    if (checked) {
      this.selectedTabs.add(tab);
    } else {
      this.selectedTabs.delete(tab);
    }

    this.allTabsSelected.set(this.selectedTabs.size === this.EXPORT_TABS.length);
  }

  public onDeliverableStatusChange(checked: boolean, status: EAnalyticsStatus): void {
    this.markDirty();

    if (checked) {
      this.selectedDeliverableStatuses.add(status);
    } else {
      this.selectedDeliverableStatuses.delete(status);
    }
  }

  public onObservationStatusChange(checked: boolean, status: TObservationStatus): void {
    this.markDirty();

    if (checked) {
      this.selectedObservationStatuses.add(status);
    } else {
      this.selectedObservationStatuses.delete(status);
    }
  }

  public onContentStatusChange(checked: boolean, status: TTileContentStatus): void {
    this.markDirty();

    if (checked) {
      this.selectedContentStatuses.add(status);
    } else {
      this.selectedContentStatuses.delete(status);
    }
  }

  public onKpiStatusChange(checked: boolean, status: EKpiGoalsStatusBadge): void {
    this.markDirty();

    if (checked) {
      this.selectedKpiStatuses.add(status);
    } else {
      this.selectedKpiStatuses.delete(status);
    }
  }

  public onContentMatchingTypeChange(checked: boolean, matchingType: EMatchingType): void {
    this.markDirty();

    if (checked) {
      this.selectedContentMatchingTypes.add(matchingType);
    } else {
      this.selectedContentMatchingTypes.delete(matchingType);
    }
  }

  public onExportDateRangeChanged(dateRange: Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'>): void {
    this.markDirty();
    this.exportStartDate.set(dateRange.startDate);
    this.exportEndDate.set(dateRange.endDate);
  }

  public onExportSelectedRangeChanged(selectedRange: EDateRangeType): void {
    this.markDirty();
    this.exportSelectedDateRange.set(selectedRange);

    if (selectedRange !== EDateRangeType.custom) {
      this.exportStartDate.set(null);
      this.exportEndDate.set(null);

      return;
    }

    if (!this.exportStartDate() && !this.exportEndDate()) {
      const campaignAnalytics = this.campaignAnalytics();
      this.exportStartDate.set(campaignAnalytics?.start ?? null);
      this.exportEndDate.set(campaignAnalytics?.end ?? null);
    }
  }

  public onDetailLevelSelect(detailLevel: EDetailLevel): void {
    this.form.get('detailLevel')?.setValue(detailLevel);
  }

  public onBudgetToggle(checked: boolean): void {
    this.form.get('budget')?.setValue(checked);
  }

  public onPanelToggle(panelId: string, expanded: boolean): void {
    SignalSetUtilitiesService.updateSetItems(this.expandedPanels, expanded ? [panelId] : [], expanded ? [] : [panelId]);
  }

  public expandCollapseVisibleData(): void {
    if (this.allVisibleDataExpanded()) {
      this.expandedPanels.set(new Set<string>());
    } else {
      SignalSetUtilitiesService.updateSetItems(this.expandedPanels, [...this.PANEL_IDS], [], new Set<string>());
    }
  }

  public selectAllKpis(): void {
    this.markDirty();
    this.selectedKpis = new Set(this.kpis().map((kpi) => kpi));
    this.allKpisSelected.set(true);
  }

  public unselectAllKpis(): void {
    this.markDirty();
    this.selectedKpis.clear();
    this.allKpisSelected.set(false);
  }

  public selectAllCreators(): void {
    this.markDirty();
    this.selectedCreatorIds = new Set(this.creators().map((creator) => creator.id));
    this.allCreatorsSelected.set(true);
  }

  public unselectAllCreators(): void {
    this.markDirty();
    this.selectedCreatorIds.clear();
    this.allCreatorsSelected.set(false);
  }

  private save(isActive: boolean): void {
    this.saving.set(true);
    this.store.setSavingExport(true);

    this.campaignExportService.updateCampaignExport(this.buildCampaignExportPayload(isActive)).subscribe({
      error: (error: HttpErrorResponse) => {
        this.saving.set(false);
        this.store.setSavingExport(false);

        const errorDetails = this.errorFormatterService.getErrorDetails(error.error as IApiError);
        const nameAlreadyExists = errorDetails.some((detail) => detail.code === 'campaign_export_name_already_exists');

        if (nameAlreadyExists) {
          return;
        }

        const errorKey = this.publishClicked()
          ? 'pages.campaignDetails.export.publishError'
          : this.unpublishClicked()
            ? 'pages.campaignDetails.export.unpublishError'
            : 'pages.campaignDetails.export.saveError';

        this.snackbarService.openError(this.translate.instant(errorKey));
      },
      next: (updatedCampaignExport) => {
        this.saving.set(false);
        this.store.setSavingExport(false);
        // Re-hydrate from the authoritative server response so the header badges reflect
        // exactly what was persisted, not just what was locally edited.
        this.applyCampaignExport(updatedCampaignExport);
        this.formChanged.set(false);
        this.saved.emit(updatedCampaignExport);

        const reportProperties = {
          campaignId: this.campaignAnalytics()?.id,
          campaignReportId: updatedCampaignExport.id ?? undefined,
          reportCustomConfigs: this.customConfigsCount(),
          reportInsightLevel: updatedCampaignExport.detailLevel,
          reportTimeRangeActive: this.exportSelectedDateRange() === EDateRangeType.custom,
        };

        if (this.publishClicked()) {
          this.onCopyLinkClick(false);
          this.snackbarService.openSuccess(
            this.translate.instant('pages.campaignDetails.export.successfullyPublishedAndLinkCopied'),
          );
          this.eventTrackingService.track(EEventType.campaignReportPublished, reportProperties);
        } else if (this.unpublishClicked()) {
          this.snackbarService.openSuccess(
            this.translate.instant('pages.campaignDetails.export.successfullyUnpublished'),
          );
          this.eventTrackingService.track(EEventType.campaignReportUnpublished, reportProperties);
        } else if (this.saveClicked()) {
          this.eventTrackingService.track(EEventType.campaignReportSaved, reportProperties);
        }
      },
    });
  }

  private markDirty(): void {
    this.formChanged.set(true);
    this.changeTick.update((tick) => tick + 1);
  }

  private nameSubscription(): void {
    this.form.controls.name.valueChanges.subscribe((name) => {
      if (!name || name.trim() === '') {
        this.form.controls.name.setValue(null, { emitEvent: false });
      }
    });
  }

  /** Appends "(Copy)"/"(Copy N)" until the result doesn't collide with any name in `existingNames`. */
  private generateCloneName(baseName: string, existingNames: string[]): string {
    const existingNamesSet = new Set(existingNames.map((name) => name.toLowerCase()));

    let count = 2;
    let name = this.translate.instant('pages.campaignDetails.export.cloneName', { name: baseName }) as string;

    while (existingNamesSet.has(name.toLowerCase())) {
      name = this.translate.instant('pages.campaignDetails.export.cloneNameWithCount', { count, name: baseName });
      count++;
    }

    return name;
  }

  /** Strips identity fields from child rows so the backend creates fresh rows for the clone
   *  instead of reassigning the source report's existing rows to it. */
  private cloneChildren<T extends { id?: string | null; campaignExportId?: string | null }>(items: T[] | null): T[] {
    return (items ?? []).map((item) => ({ ...item, campaignExportId: null, id: null }));
  }

  private buildCampaignExportPayload(isActive: boolean): ICampaignExportApi {
    const campaignExport = this.campaignExport();

    return {
      ...campaignExport,
      ...this.form.value,
      allCreatorsSelected: this.allCreatorsSelected(),
      allKpisSelected: this.allKpisSelected(),
      allPlatformFeaturesSelected: this.allPlatformFeaturesSelected(),
      allTabsSelected: this.allTabsSelected(),
      campaignId: this.campaignAnalytics()?.id,
      contentMatchingTypes: this.buildContentMatchingTypesPayload(),
      contentStatuses: this.buildContentStatusesPayload(),
      creators: this.campaignExportDialogService.getSelectedCreators(
        campaignExport,
        this.selectedCreatorIds,
        this.allCreatorsSelected(),
      ),
      deliverableStatuses: this.buildDeliverableStatusesPayload(),
      detailLevel: this.form.get('detailLevel')?.value || EDetailLevel.generalInfo,
      end: this.getExportDateRangeValue(this.exportEndDate()),
      id: campaignExport.id,
      isActive,
      kpis: this.campaignExportDialogService.getSelectedKpis(campaignExport, this.selectedKpis, this.allKpisSelected()),
      kpiStatuses: this.buildKpiStatusesPayload(),
      name: this.form.get('name')?.value || campaignExport.name,
      observationStatuses: this.buildObservationStatusesPayload(),
      platformFeatures: this.campaignExportDialogService.getSelectedPlatformFeatures(
        campaignExport,
        this.selectedPlatformFeatures,
        this.allPlatformFeaturesSelected(),
      ),
      showBudget: this.form.get('budget')?.value || false,
      showContentMatchingType: this.selectedContentMatchingTypes.size > 0,
      showContentStatus: this.selectedContentStatuses.size > 0,
      showDeliverableStatus: this.selectedDeliverableStatuses.size > 0,
      showKpiStatus: this.selectedKpiStatuses.size > 0,
      showObservationStatus: this.selectedObservationStatuses.size > 0,
      start: this.getExportDateRangeValue(this.exportStartDate()),
      tabs: this.campaignExportDialogService.getSelectedTabs(campaignExport, this.selectedTabs, this.allTabsSelected()),
    };
  }

  private getExportDateRangeValue(date: Date | null): string | null {
    // allTime is sent as null on both ends so the backend applies no date filtering at all.
    if (this.exportSelectedDateRange() === EDateRangeType.allTime || !date) {
      return null;
    }

    return this.dateUtilitiesService.formatToISOSeconds(date);
  }

  private buildDeliverableStatusesPayload(): ICampaignExportDeliverableStatusApi[] {
    if (this.selectedDeliverableStatuses.size === this.DELIVERABLE_STATUSES.length) {
      return [];
    }

    return Array.from(this.selectedDeliverableStatuses).map((status) => {
      const existing = this.campaignExport().deliverableStatuses?.find((ds) => ds.status === status);

      return { id: existing?.id || null, status };
    });
  }

  private buildObservationStatusesPayload(): ICampaignExportObservationStatusApi[] {
    if (this.selectedObservationStatuses.size === this.OBSERVATION_STATUSES.length) {
      return [];
    }

    return Array.from(this.selectedObservationStatuses).map((displayStatus) => {
      const apiStatus = this.OBSERVATION_STATUS_TO_API[displayStatus];
      const existing = this.campaignExport().observationStatuses?.find((os) => os.status === apiStatus);

      return { id: existing?.id || null, status: apiStatus };
    });
  }

  private buildContentStatusesPayload(): ICampaignExportContentStatusApi[] {
    if (this.selectedContentStatuses.size === this.CONTENT_STATUSES.length) {
      return [];
    }

    return Array.from(this.selectedContentStatuses).map((displayStatus) => {
      const apiStatus = this.CONTENT_STATUS_TO_API[displayStatus];
      const existing = this.campaignExport().contentStatuses?.find((cs) => cs.status === apiStatus);

      return { id: existing?.id || null, status: apiStatus };
    });
  }

  private buildKpiStatusesPayload(): ICampaignExportKpiStatusApi[] {
    if (this.selectedKpiStatuses.size === this.KPI_STATUSES.length) {
      return [];
    }

    return Array.from(this.selectedKpiStatuses).map((displayStatus) => {
      const apiStatus = this.KPI_STATUS_TO_API[displayStatus]!;
      const existing = this.campaignExport().kpiStatuses?.find((ks) => ks.status === apiStatus);

      return { id: existing?.id || null, status: apiStatus };
    });
  }

  private buildContentMatchingTypesPayload(): ICampaignExportContentMatchingTypeApi[] {
    if (this.selectedContentMatchingTypes.size === this.CONTENT_MATCHING_TYPES.length) {
      return [];
    }

    return Array.from(this.selectedContentMatchingTypes).map((matchingType) => {
      const existing = this.campaignExport().contentMatchingTypes?.find((cm) => cm.matchingType === matchingType);

      return { id: existing?.id || null, matchingType };
    });
  }

  private applyCampaignExport(campaignExport: ICampaignExportApi): void {
    this.form.patchValue({
      budget: campaignExport.showBudget,
      detailLevel: campaignExport.detailLevel || EDetailLevel.generalInfo,
      link: this.generateLink(),
      name: campaignExport.name || '',
    });

    this.baselineName.set(campaignExport.name || '');
    this.baselineBudget.set(!!campaignExport.showBudget);
    this.baselineDetailLevel.set(campaignExport.detailLevel || EDetailLevel.generalInfo);
    this.editableTitle().syncOriginalTitle(campaignExport.name || '');

    this.resolveEntitySelections(campaignExport);
    this.resolveStatusSelections(campaignExport);
    this.resolveDateRangeSelection(campaignExport);
    this.changeTick.update((tick) => tick + 1);
  }

  private resolveDateRangeSelection(campaignExport: ICampaignExportApi): void {
    if (campaignExport.start && campaignExport.end) {
      this.exportSelectedDateRange.set(EDateRangeType.custom);
      this.exportStartDate.set(new Date(campaignExport.start));
      this.exportEndDate.set(new Date(campaignExport.end));

      return;
    }

    this.exportSelectedDateRange.set(EDateRangeType.allTime);
    this.exportStartDate.set(null);
    this.exportEndDate.set(null);
  }

  private resolveEntitySelections(campaignExport: ICampaignExportApi): void {
    const allKpisSelected = !!campaignExport.allKpisSelected;
    const allCreatorsSelected = !!campaignExport.allCreatorsSelected;
    const allPlatformFeaturesSelected = !!campaignExport.allPlatformFeaturesSelected;
    const allTabsSelected = campaignExport.allTabsSelected !== false;

    this.selectedKpis = new Set(
      allKpisSelected ? this.kpis().map((kpi) => kpi) : campaignExport.kpis?.map((kpi) => kpi.kpi) || [],
    );
    this.selectedCreatorIds = new Set(
      allCreatorsSelected
        ? this.creators().map((creator) => creator.id)
        : campaignExport.creators?.map((creator) => creator.creatorId) || [],
    );
    this.selectedPlatformFeatures = new Set(
      allPlatformFeaturesSelected
        ? this.platformFeatures().map((pf) => pf)
        : campaignExport.platformFeatures?.map((pf) => camelCase(pf.platformFeature) as EPlatformFeature) || [],
    );
    this.selectedTabs = new Set(
      allTabsSelected
        ? this.EXPORT_TABS
        : (campaignExport.tabs?.map((t) => t.tab).filter((tab) => this.EXPORT_TABS.includes(tab)) ?? []),
    );

    this.allKpisSelected.set(allKpisSelected);
    this.allCreatorsSelected.set(allCreatorsSelected);
    this.allPlatformFeaturesSelected.set(allPlatformFeaturesSelected);
    this.allTabsSelected.set(allTabsSelected);
  }

  private resolveStatusSelections(campaignExport: ICampaignExportApi): void {
    // showXxx=false → none selected (empty set).
    // showXxx=true + empty array → all selected.
    // showXxx=true + non-empty array → subset.
    this.selectedDeliverableStatuses = campaignExport.showDeliverableStatus
      ? new Set(
          campaignExport.deliverableStatuses?.length
            ? campaignExport.deliverableStatuses.map((ds) => ds.status)
            : this.DELIVERABLE_STATUSES,
        )
      : new Set();

    this.selectedObservationStatuses = campaignExport.showObservationStatus
      ? new Set(
          campaignExport.observationStatuses?.length
            ? campaignExport.observationStatuses
                .map((os) => this.API_TO_OBSERVATION_STATUS[os.status])
                .filter((s): s is TObservationStatus => s !== undefined)
            : this.OBSERVATION_STATUSES,
        )
      : new Set();

    this.selectedContentStatuses = campaignExport.showContentStatus
      ? new Set(
          campaignExport.contentStatuses?.length
            ? campaignExport.contentStatuses
                .map((cs) => this.API_TO_CONTENT_STATUS[cs.status])
                .filter((s): s is TTileContentStatus => s !== undefined)
            : this.CONTENT_STATUSES,
        )
      : new Set();

    this.selectedKpiStatuses = campaignExport.showKpiStatus
      ? new Set(
          campaignExport.kpiStatuses?.length
            ? campaignExport.kpiStatuses
                .map((ks) => this.API_TO_KPI_STATUS[ks.status])
                .filter((s): s is EKpiGoalsStatusBadge => s !== undefined)
            : this.KPI_STATUSES,
        )
      : new Set();

    this.selectedContentMatchingTypes = campaignExport.showContentMatchingType
      ? new Set(
          campaignExport.contentMatchingTypes?.length
            ? campaignExport.contentMatchingTypes.map((cm) => cm.matchingType)
            : this.CONTENT_MATCHING_TYPES,
        )
      : new Set();
  }

  // Demo route (campaigns/demo/:id) reuses this exact export dialog - if we're inside that demo
  // tree, the copy-link/preview URL must point at the demo-mode export route
  // (campaigns/demo/export/:id, backed by fake services) instead of the real
  // campaigns/export/:id, which would otherwise 404 (or worse, hit the real backend) against a
  // fake in-memory id that only the demo fakes know about.
  private generateLink(): string {
    const campaignExport = untracked(this.campaignExport);

    if (!campaignExport?.id) {
      return '';
    }

    const isDemo = this.router.url.startsWith('/campaigns/demo/');
    const relativePath = isDemo
      ? `/campaigns/demo/export/${campaignExport.id}`
      : `/campaigns/export/${campaignExport.id}`;

    return `${WindowRedirectService.getBaseUrl()}${relativePath}`;
  }
}
